import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { SelectOption } from '@models/ui-models/select-options';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { ABNPipe } from '../../../shared/pipes/abnPipe';
import { CompanyChangeNameComponent } from '../../asic-forms/company';

export const privateLegalElementsOptions: SelectOption[] = [
  'Pty Ltd',
  'Pty Ltd.',
  'Pty Limited',
  'Pty. Ltd.',
  'Pty. Ltd',
  'Pty. Limited',
  'Proprietary Ltd',
  'Proprietary Ltd.',
  'Proprietary Limited'
].map(legalElement => ({ label: legalElement, value: legalElement }));

export const publicLegalElementsOptions: SelectOption[] = [
  'Pty Ltd',
  'Pty Ltd.',
  'Pty Limited',
  'Pty. Ltd.',
  'Pty. Ltd',
  'Pty. Limited',
  'Proprietary Ltd',
  'Proprietary Ltd.',
  'Proprietary Limited'
].map(legalElement => ({ label: legalElement, value: legalElement }));

export const legalElements: Record<'public' | 'proprietary', SelectOption[]> = {
  public: publicLegalElementsOptions,
  proprietary: privateLegalElementsOptions
};

export class CompanyChangeName extends EntityChangeData {
  static override readonly $type = 'CompanyChangeName';
  newCompanyName: string;
  useAcnAsNewCompanyName: boolean;
  newLegalElementsIfAcnIsUsed: string;
  resolutionText: string;
  newNameReservedWithForm410: boolean;
  isProposedNameIdenticalToRegisteredBusinessName: boolean;
  businessNameHolderAbn: string;
  manualReviewRequested: boolean;
  manualProcessingReason: string;
  paymentMethod: PaymentMethod;
  reservationNumber: string;

  constructor(data: Partial<CompanyChangeName> = {}) {
    super(data);

    this.newCompanyName = data.newCompanyName ?? '';
    this.newLegalElementsIfAcnIsUsed = data.newLegalElementsIfAcnIsUsed ?? '';
    this.resolutionText = data.resolutionText ?? '';
    this.newNameReservedWithForm410 = data.newNameReservedWithForm410 ?? false;
    this.isProposedNameIdenticalToRegisteredBusinessName = data.isProposedNameIdenticalToRegisteredBusinessName ?? false;
    this.businessNameHolderAbn = data.businessNameHolderAbn ?? '';
    this.manualReviewRequested = data.manualReviewRequested ?? false;
    this.manualProcessingReason = data.manualProcessingReason ?? '';
    this.paymentMethod = data.paymentMethod ?? PaymentMethod.INV;
    this.reservationNumber = data.reservationNumber ?? '';
    this.useAcnAsNewCompanyName = data.useAcnAsNewCompanyName ?? false;
  }

  override toDictionary(): { key: string; value: string; }[] {
    const dict = new ChangeDictionaryHelper();

    dict
      .addYesNo('Use ACN as new company name', this.useAcnAsNewCompanyName)
      .add('Old Company Name', ChangeDictionaryHelper.COMPANY_NAME)
      .add('New Company Name', this.newCompanyName);

    if (!this.useAcnAsNewCompanyName) {
      // Use ACN as proposed company name? === No
      dict
        .addYesNo('Has a name reservation been lodged to reserve the proposed new company name?', this.newNameReservedWithForm410)
        .addIfValueIsNotEmpty('Reservation Number', this.reservationNumber)
        .addYesNo('Is the proposed name identical to a registered business name(s)?', this.isProposedNameIdenticalToRegisteredBusinessName)

      if (this.isProposedNameIdenticalToRegisteredBusinessName && this.businessNameHolderAbn) {
        dict.addIfValueIsNotEmpty('ABN', this.businessNameHolderAbn ? new ABNPipe().transform(this.businessNameHolderAbn) : '')
      }
    }

    dict.addYesNo('Would you like ASIC to manually review the name change?', this.manualReviewRequested);

    if (this.manualReviewRequested && this.manualProcessingReason.trim()) {
      dict.addIfValueIsNotEmpty('Manual Processing Reason', this.manualProcessingReason);
    }

    dict
      .add('Payment Method', paymentMethodsNames[this.paymentMethod])
      .addDate('Resolution Date', this.changeDate)
      .add('Special Resolution', this.resolutionText);

    return dict.dictionary;
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    (modalRef.componentInstance as CompanyChangeNameComponent).companyChangeData.documentId = document.documentId;
    return modalRef;
  }

  override fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit: boolean): NgbModalRef {
    const instance = modalRef.componentInstance as CompanyChangeNameComponent;

    instance.isEdit = isEdit;
    instance.companyChangeData = new CompanyChangeData(company);
    instance.formModel = structuredClone(this);

    return modalRef;
  }
}

// Payment method used for the form 205A
export enum PaymentMethod {
  DD = 0, // Direct debit
  INV = 1 // Invoice
}

export const paymentMethodsNames: Record<PaymentMethod, string> = {
  [PaymentMethod.DD]: 'Direct debit',
  [PaymentMethod.INV]: 'Invoice',
};

export const paymentMethodOptions: SelectOption[] = [
  { label: paymentMethodsNames[PaymentMethod.DD], value: PaymentMethod.DD },
  { label: paymentMethodsNames[PaymentMethod.INV], value: PaymentMethod.INV },
];
